import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CAREER_JOURNEY_STEPS } from "@/data/homepageData";

const HERO_BG_TEXTURE = "https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZWNoJTIwZ2xvd3xlbnwwfHx8fDE3ODQxNzY5Mjl8MA&ixlib=rb-4.1.0&q=85";

/** Shared Career Journey visual — `variant="compact"` for the Hero (a "Vertical Glowing
 * Timeline Track" showing all 8 steps at once, auto-cycling which one is expanded — no
 * empty space, no collision with the fixed AI Mentor widget thanks to the bottom mask-fade),
 * `variant="full"` for the interactive "How Cloudivo Works" timeline (click any step). */
export const CareerJourneyTimeline = ({ variant = "full" }) => {
  const [active, setActive] = useState(0);
  const compact = variant === "compact";

  useEffect(() => {
    if (!compact) return;
    const t = setInterval(() => setActive((a) => (a + 1) % CAREER_JOURNEY_STEPS.length), 2200);
    return () => clearInterval(t);
  }, [compact]);

  const step = CAREER_JOURNEY_STEPS[active];

  if (compact) {
    return (
      <div
        className="relative h-[560px] rounded-3xl border border-white/10 bg-white/5 backdrop-blur-2xl backdrop-saturate-150 shadow-[0_0_40px_rgba(92,157,255,0.1)] overflow-hidden"
        data-testid="career-journey-compact"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none bg-cover bg-center blur-sm"
          style={{ backgroundImage: `url(${HERO_BG_TEXTURE})` }}
        />
        <div className="relative h-full flex flex-col px-6 pt-7 pb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400 mb-6 shrink-0">Your Career Journey</span>

          <div
            className="relative flex-1 overflow-hidden"
            style={{ maskImage: "linear-gradient(to bottom, black 72%, transparent 96%)", WebkitMaskImage: "linear-gradient(to bottom, black 72%, transparent 96%)" }}
          >
            {/* Continuous vertical track + animated gradient tracer to the active node */}
            <div className="absolute left-[19px] top-1 bottom-1 w-px bg-white/10" />
            <motion.div
              className="absolute left-[19px] top-1 w-1 -ml-[1.5px] rounded-full bg-gradient-to-b from-[#5C9DFF] to-[#B58BFF]"
              initial={false}
              animate={{ height: `${((active + 1) / CAREER_JOURNEY_STEPS.length) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              data-testid="hero-timeline-tracer"
            />

            <div className="relative flex flex-col">
              {CAREER_JOURNEY_STEPS.map((s, i) => {
                const isActive = i === active;
                return (
                  <div key={s.key} className="relative flex items-start gap-4 py-2" data-testid={`hero-timeline-node-${s.key}${isActive ? "-active" : ""}`}>
                    <div className="relative shrink-0 mt-0.5">
                      {isActive && (
                        <motion.span
                          className="absolute -inset-1.5 rounded-full"
                          style={{ background: `${s.color}30` }}
                          animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0.15, 0.6] }}
                          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                      )}
                      <div
                        className="relative w-[13px] h-[13px] rounded-full border-2 flex items-center justify-center"
                        style={{ borderColor: isActive ? s.color : "rgba(255,255,255,0.2)", background: isActive ? s.color : "#0A0A0F" }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 pb-1">
                      <div className="flex items-center gap-2">
                        <s.icon className="w-3.5 h-3.5 shrink-0" style={{ color: isActive ? s.color : "rgba(255,255,255,0.35)" }} strokeWidth={1.75} />
                        <p className={`text-sm font-semibold transition-colors ${isActive ? "text-white" : "text-white/40"}`}>{s.label}</p>
                      </div>
                      <AnimatePresence initial={false}>
                        {isActive && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="text-xs text-slate-400 mt-1.5 leading-relaxed max-w-[260px] overflow-hidden"
                          >
                            {s.desc}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="career-journey-full">
      <div className="relative">
        {/* Connecting track behind the step pills, with a gradient fill up to the active step */}
        <div className="absolute top-[27px] left-0 right-0 h-px bg-white/10" />
        <motion.div
          className="absolute top-[27px] left-0 h-[2px] rounded-full bg-gradient-to-r from-[#5C9DFF] to-[#B58BFF]"
          initial={false}
          animate={{ width: `${(active / (CAREER_JOURNEY_STEPS.length - 1)) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        />
        <div className="relative flex overflow-x-auto scrollbar-hide gap-3 pb-2">
          {CAREER_JOURNEY_STEPS.map((s, i) => {
            const isActive = i === active;
            const isDone = i < active;
            return (
              <motion.button
                key={s.key} onClick={() => setActive(i)} data-testid={`journey-step-${s.key}`}
                whileHover={{ y: -3 }} whileTap={{ scale: 0.96 }}
                className="relative shrink-0 flex flex-col items-center gap-2.5 px-4 py-4 rounded-2xl border transition-colors duration-300"
                style={{
                  minWidth: "112px",
                  borderColor: isActive ? `${s.color}70` : "rgba(255,255,255,0.1)",
                  background: isActive ? `linear-gradient(160deg, ${s.color}20, rgba(255,255,255,0.02))` : "rgba(255,255,255,0.02)",
                  boxShadow: isActive ? `0 8px 24px ${s.color}25` : "none",
                }}
              >
                <span
                  className="absolute -top-1.5 -left-1.5 w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center border"
                  style={{
                    background: isDone ? s.color : isActive ? `${s.color}30` : "#0A0A0F",
                    borderColor: isDone || isActive ? s.color : "rgba(255,255,255,0.15)",
                    color: isDone ? "#0A0A0F" : isActive ? s.color : "rgba(255,255,255,0.35)",
                  }}
                >
                  {isDone ? "✓" : i + 1}
                </span>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300"
                  style={{ background: `${s.color}1f`, transform: isActive ? "scale(1.08)" : "scale(1)" }}
                >
                  <s.icon className="w-4.5 h-4.5" style={{ color: isActive || isDone ? s.color : "rgba(255,255,255,0.45)" }} strokeWidth={1.75} />
                </div>
                <span className={`text-[11px] font-semibold text-center leading-tight transition-colors ${isActive ? "text-white" : "text-slate-400"}`}>{s.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
          className="relative mt-6 rounded-3xl border p-7 sm:p-8 overflow-hidden"
          style={{ borderColor: `${step.color}30`, background: `linear-gradient(135deg, ${step.color}12, rgba(255,255,255,0.02))` }}
          data-testid="journey-step-detail"
        >
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: step.color }} />
          <div className="relative flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${step.color}22`, border: `1px solid ${step.color}45`, boxShadow: `0 0 30px ${step.color}30` }}>
              <step.icon className="w-6 h-6" style={{ color: step.color }} strokeWidth={1.75} />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: step.color }}>Step {active + 1} of {CAREER_JOURNEY_STEPS.length}</span>
              <p className="text-xl font-heading font-semibold text-white mt-1">{step.label}</p>
              <p className="text-sm text-slate-400 mt-2 max-w-md leading-relaxed">{step.desc}</p>
              <Link
                to={step.route} data-testid={`journey-step-cta-${step.key}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold mt-4 group"
                style={{ color: step.color }}
              >
                Try {step.label} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
